import Copy from "@/components/copy";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  useApplyForReferral,
  useWithdrawReferralEarnings,
} from "@/hooks/use-mutations";
import {
  useGetReferralApplication,
  useGetReferralDetails,
  useGetReferralList,
} from "@/hooks/use-queries";
import { handleShare } from "@/lib/helper";
import { formatAmount } from "@/lib/utils";
import { useUser } from "@/zustand/store";
import {
  AlertCircle,
  BadgeCheck,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Link2,
  Lock,
  Mail,
  Search,
  Share2,
} from "lucide-react";
import { useMemo, useState, type FC, type ReactNode } from "react";

/* Multi-colour art, so these are inline SVGs rather than `nav-icon` masks —
   the mask path collapses a drawing to a single colour and these carry their
   own gradients. Kept local to the screen; nothing else uses them. */

const ReferralsIcon = () => (
  <svg width="24" height="22" viewBox="0 0 24 22" fill="none" aria-hidden>
    <rect width="24" height="22" rx="5" fill="#8AB4FF" fillOpacity="0.12" />
    <path
      d="M12 4.5C11.4033 4.5 10.831 4.73705 10.409 5.15901C9.98705 5.58097 9.75 6.15326 9.75 6.75C9.75 7.34674 9.98705 7.91903 10.409 8.34099C10.831 8.76295 11.4033 9 12 9C12.5967 9 13.169 8.76295 13.591 8.34099C14.0129 7.91903 14.25 7.34674 14.25 6.75C14.25 6.15326 14.0129 5.58097 13.591 5.15901C13.169 4.73705 12.5967 4.5 12 4.5Z"
      fill="url(#referrals_a)"
    />
    <path
      opacity="0.5"
      d="M7 6C6.46957 6 5.96086 6.21071 5.58579 6.58579C5.21071 6.96086 5 7.46957 5 8C5 8.53043 5.21071 9.03914 5.58579 9.41421C5.96086 9.78929 6.46957 10 7 10C7.53043 10 8.03914 9.78929 8.41421 9.41421C8.78929 9.03914 9 8.53043 9 8C9 7.46957 8.78929 6.96086 8.41421 6.58579C8.03914 6.21071 7.53043 6 7 6Z"
      fill="url(#referrals_b)"
    />
    <path
      opacity="0.5"
      d="M17 6C16.4696 6 15.9609 6.21071 15.5858 6.58579C15.2107 6.96086 15 7.46957 15 8C15 8.53043 15.2107 9.03914 15.5858 9.41421C15.9609 9.78929 16.4696 10 17 10C17.5304 10 18.0391 9.78929 18.4142 9.41421C18.7893 9.03914 19 8.53043 19 8C19 7.46957 18.7893 6.96086 18.4142 6.58579C18.0391 6.21071 17.5304 6 17 6Z"
      fill="url(#referrals_c)"
    />
    <path
      opacity="0.5"
      d="M14.6918 10.6481C14.5332 10.6056 14.3678 10.5947 14.2051 10.6162C14.0423 10.6376 13.8853 10.6909 13.7432 10.773C13.601 10.855 13.4764 10.9643 13.3765 11.0946C13.2765 11.2248 13.2032 11.3735 13.1608 11.5321L12.7078 13.2221C12.5018 13.9906 12.6095 14.8095 13.0073 15.4986C13.405 16.1877 14.0602 16.6906 14.8288 16.8966C15.5973 17.1026 16.4162 16.9948 17.1053 16.5971C17.7944 16.1993 18.2973 15.5441 18.5033 14.7756L18.9563 13.0851C18.9988 12.9265 19.0096 12.7611 18.9882 12.5983C18.9668 12.4356 18.9135 12.2786 18.8314 12.1365C18.7493 11.9943 18.64 11.8697 18.5098 11.7698C18.3795 11.6698 18.2309 11.5965 18.0723 11.5541L14.6918 10.6481Z"
      fill="url(#referrals_d)"
    />
    <path
      opacity="0.5"
      d="M9.3076 10.6481C9.46617 10.6056 9.63156 10.5947 9.79432 10.6162C9.95709 10.6376 10.114 10.6909 10.2562 10.773C10.3984 10.855 10.523 10.9643 10.6229 11.0946C10.7228 11.2248 10.7961 11.3735 10.8386 11.5321L11.2916 13.2221C11.3936 13.6026 11.4197 13.9996 11.3684 14.3902C11.317 14.7809 11.1892 15.1576 10.9922 15.4988C10.7953 15.8401 10.533 16.1392 10.2205 16.3791C9.90792 16.619 9.55117 16.795 9.1706 16.8971C8.79003 16.9991 8.39309 17.0252 8.00244 16.9738C7.61179 16.9225 7.23508 16.7946 6.89383 16.5977C6.55257 16.4007 6.25345 16.1385 6.01354 15.8259C5.77364 15.5134 5.59764 15.1566 5.4956 14.7761L5.0426 13.0856C5.00011 12.927 4.98927 12.7616 5.01069 12.5988C5.03212 12.4361 5.0854 12.2791 5.16749 12.137C5.24958 11.9948 5.35887 11.8702 5.48912 11.7703C5.61937 11.6703 5.76803 11.597 5.9266 11.5546L9.3076 10.6481Z"
      fill="url(#referrals_e)"
    />
    <path
      d="M10.25 10C9.91848 10 9.60054 10.1317 9.36612 10.3661C9.1317 10.6005 9 10.9185 9 11.25V14C9 14.7956 9.31607 15.5587 9.87868 16.1213C10.4413 16.6839 11.2044 17 12 17C12.7956 17 13.5587 16.6839 14.1213 16.1213C14.6839 15.5587 15 14.7956 15 14V11.25C15 10.9185 14.8683 10.6005 14.6339 10.3661C14.3995 10.1317 14.0815 10 13.75 10H10.25Z"
      fill="url(#referrals_f)"
    />
    <defs>
      <linearGradient
        id="referrals_a"
        x1="10.579"
        y1="4.57904"
        x2="14.635"
        y2="7.81944"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
      <linearGradient
        id="referrals_b"
        x1="5.7369"
        y1="6.07025"
        x2="9.34225"
        y2="8.95061"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
      <linearGradient
        id="referrals_c"
        x1="15.7369"
        y1="6.07025"
        x2="19.3422"
        y2="8.95061"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
      <linearGradient
        id="referrals_d"
        x1="13.7833"
        y1="10.7178"
        x2="19.5459"
        y2="15.3216"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
      <linearGradient
        id="referrals_e"
        x1="6.17793"
        y1="10.7178"
        x2="11.941"
        y2="15.322"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
      <linearGradient
        id="referrals_f"
        x1="10.1054"
        y1="10.1229"
        x2="16.1368"
        y2="14.2532"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
    </defs>
  </svg>
);

const EarningsIcon = () => (
  <svg width="24" height="22" viewBox="0 0 24 22" fill="none" aria-hidden>
    <rect width="24" height="22" rx="5" fill="#8AB4FF" fillOpacity="0.12" />
    <path
      opacity="0.4"
      d="M16.026 12.0337C15.746 12.307 15.586 12.7003 15.626 13.1203C15.686 13.8403 16.346 14.367 17.066 14.367H18.3327V15.1603C18.3327 16.5403 17.206 17.667 15.826 17.667H8.17268C6.79268 17.667 5.66602 16.5403 5.66602 15.1603V10.6737C5.66602 9.29367 6.79268 8.16699 8.17268 8.16699H15.826C17.206 8.16699 18.3327 9.29367 18.3327 10.6737V11.6337H16.986C16.6127 11.6337 16.2727 11.7803 16.026 12.0337Z"
      fill="url(#earnings_a)"
    />
    <path
      d="M13.8993 5.63342V8.16674H8.17268C6.79268 8.16674 5.66602 9.29341 5.66602 10.6734V8.22676C5.66602 7.43343 6.15268 6.72673 6.89268 6.44673L12.186 4.44673C13.0127 4.14006 13.8993 4.74675 13.8993 5.63342Z"
      fill="url(#earnings_b)"
    />
    <path
      d="M19.0405 12.3138V13.6871C19.0405 14.0538 18.7472 14.3538 18.3738 14.3671H17.0672C16.3472 14.3671 15.6872 13.8404 15.6272 13.1204C15.5872 12.7004 15.7472 12.3071 16.0272 12.0338C16.2738 11.7804 16.6138 11.6338 16.9872 11.6338H18.3738C18.7472 11.6471 19.0405 11.9471 19.0405 12.3138Z"
      fill="url(#earnings_c)"
    />
    <path
      d="M13.3327 11.5H8.66602C8.39268 11.5 8.16602 11.2733 8.16602 11C8.16602 10.7267 8.39268 10.5 8.66602 10.5H13.3327C13.606 10.5 13.8327 10.7267 13.8327 11C13.8327 11.2733 13.606 11.5 13.3327 11.5Z"
      fill="url(#earnings_d)"
    />
    <defs>
      <linearGradient
        id="earnings_a"
        x1="7.99954"
        y1="8.33384"
        x2="16.7614"
        y2="17.6672"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
      <linearGradient
        id="earnings_b"
        x1="7.1828"
        y1="4.47699"
        x2="13.0065"
        y2="10.5504"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
      <linearGradient
        id="earnings_c"
        x1="16.251"
        y1="11.6818"
        x2="18.777"
        y2="14.2064"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
      <linearGradient
        id="earnings_d"
        x1="9.20996"
        y1="10.5176"
        x2="9.59923"
        y2="12.2799"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E3F7FF" />
        <stop offset="1" stopColor="#D3BBF1" />
      </linearGradient>
    </defs>
  </svg>
);

const AvailableIcon = () => (
  <svg width="24" height="22" viewBox="0 0 24 22" fill="none" aria-hidden>
    <rect width="24" height="22" rx="5" fill="#4ADE80" fillOpacity="0.12" />
    <path
      d="M6.91 15L6 14.0667L10.81 9.1L13.41 11.7667L16.79 8.33333H15.1V7H19V11H17.7V9.26667L13.41 13.6667L10.81 11L6.91 15Z"
      fill="#41B973"
    />
  </svg>
);

/* The withdraw panel's wallet mark. Same drawing as `EarningsIcon`, retinted
   green to match the panel — solid fills rather than the blue gradient, which
   is how the supplied `AvailableIcon` handles its green. */
const WithdrawWalletIcon = () => (
  <svg width="24" height="22" viewBox="0 0 24 22" fill="none" aria-hidden>
    <rect width="24" height="22" rx="5" fill="#4ADE80" fillOpacity="0.12" />
    <path
      opacity="0.4"
      d="M16.026 12.0337C15.746 12.307 15.586 12.7003 15.626 13.1203C15.686 13.8403 16.346 14.367 17.066 14.367H18.3327V15.1603C18.3327 16.5403 17.206 17.667 15.826 17.667H8.17268C6.79268 17.667 5.66602 16.5403 5.66602 15.1603V10.6737C5.66602 9.29367 6.79268 8.16699 8.17268 8.16699H15.826C17.206 8.16699 18.3327 9.29367 18.3327 10.6737V11.6337H16.986C16.6127 11.6337 16.2727 11.7803 16.026 12.0337Z"
      fill="#41B973"
    />
    <path
      d="M13.8993 5.63342V8.16674H8.17268C6.79268 8.16674 5.66602 9.29341 5.66602 10.6734V8.22676C5.66602 7.43343 6.15268 6.72673 6.89268 6.44673L12.186 4.44673C13.0127 4.14006 13.8993 4.74675 13.8993 5.63342Z"
      fill="#41B973"
    />
    <path
      d="M19.0405 12.3138V13.6871C19.0405 14.0538 18.7472 14.3538 18.3738 14.3671H17.0672C16.3472 14.3671 15.6872 13.8404 15.6272 13.1204C15.5872 12.7004 15.7472 12.3071 16.0272 12.0338C16.2738 11.7804 16.6138 11.6338 16.9872 11.6338H18.3738C18.7472 11.6471 19.0405 11.9471 19.0405 12.3138Z"
      fill="#41B973"
    />
    <path
      d="M13.3327 11.5H8.66602C8.39268 11.5 8.16602 11.2733 8.16602 11C8.16602 10.7267 8.39268 10.5 8.66602 10.5H13.3327C13.606 10.5 13.8327 10.7267 13.8327 11C13.8327 11.2733 13.606 11.5 13.3327 11.5Z"
      fill="#41B973"
    />
  </svg>
);

const WithdrawnIcon = () => (
  <svg width="24" height="22" viewBox="0 0 24 22" fill="none" aria-hidden>
    <rect width="24" height="22" rx="5" fill="#B794F4" fillOpacity="0.12" />
    <path
      d="M12 5V12.6364M9 10.0909L12 13L15 10.0909"
      stroke="#B794F4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line x1="9" y1="15.5" x2="15" y2="15.5" stroke="#B794F4" />
  </svg>
);

interface StatCardProps {
  icon: ReactNode;
  label: string;
  value: string;
  /* The tile's surface + stroke pair. Neutral is the default; Available and
     Total Withdrawn each get their own tint. */
  tone?: "neutral" | "success" | "gold";
  footnote?: ReactNode;
}

const toneClasses: Record<NonNullable<StatCardProps["tone"]>, string> = {
  neutral: "bg-dark-stat-surface border-dark-stat-stroke",
  success: "bg-dark-stat-success-surface border-dark-stat-success-stroke",
  gold: "bg-dark-stat-gold-surface border-dark-stat-gold-stroke",
};

const StatCard: FC<StatCardProps> = ({
  icon,
  label,
  value,
  tone = "neutral",
  footnote,
}) => (
  <div className={`rounded-xl border p-3.5 ${toneClasses[tone]}`}>
    {icon}
    <p className="text-dark-text-300 mt-4 text-[11px] font-medium tracking-wide uppercase">
      {label}
    </p>
    <p className="text-dark-text-50 mt-1 text-xl font-semibold">{value}</p>
    {footnote && <div className="mt-1 text-xs">{footnote}</div>}
  </div>
);

/* One page of the referred-users table. The list endpoint takes no search
   param, so search filters the fetched page locally — see `PAGE_SIZE`. */
const PAGE_SIZE = 10;

const initialsOf = (first: string, last: string) =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();

/* "Joined 3d ago" / "Joined 2m ago". Falls back to a short date past a year,
   where a relative label stops being useful. */
const joinedLabel = (iso: string) => {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "recently";

  const days = Math.floor((Date.now() - then.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}m ago`;
  return then.toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
};

const ReferredUserRow: FC<{
  referral: ReferredUser;
  isTopEarner: boolean;
}> = ({ referral, isTopEarner }) => {
  const name = `${referral.firstName} ${referral.lastName}`.trim();
  /* The API has no "active" flag. `hasBoughtCard` is the only engagement
     signal it returns, so it stands in for Active/Inactive here and in the
     filter. */
  const active = referral.hasBoughtCard;

  return (
    <li className="bg-dark-stat-surface border-dark-stat-stroke flex items-center gap-3 rounded-xl border p-3">
      <span className="bg-dark-stat-gold-surface border-dark-stat-gold-stroke text-dark-primary-main flex size-10 shrink-0 items-center justify-center rounded-full border text-xs font-semibold">
        {initialsOf(referral.firstName, referral.lastName)}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-dark-text-50 truncate font-medium">{name}</p>
          {isTopEarner && (
            <span className="bg-dark-stat-gold-surface border-dark-stat-gold-stroke text-dark-primary-main shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-semibold tracking-wide uppercase">
              Top earner
            </span>
          )}
        </div>
        <p className="text-dark-text-300 truncate text-xs">
          {referral.email} &nbsp;•&nbsp; Joined{" "}
          {joinedLabel(referral.createdAt)}
        </p>
      </div>

      <div className="shrink-0 text-right">
        <p className="text-dark-referral-success text-xs font-medium">
          +${formatAmount(referral.earnedAmount)}
        </p>
        <span
          className={`mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
            active
              ? "bg-dark-stat-success-surface border-dark-stat-success-stroke text-dark-referral-success border"
              : "bg-dark-stat-surface border-dark-stat-stroke text-dark-text-300 border"
          }`}
        >
          <span
            className={`size-1 rounded-full ${active ? "bg-dark-referral-success" : "bg-dark-text-300"}`}
          />
          {active ? "Active" : "Inactive"}
        </span>
      </div>
    </li>
  );
};

/* The pending-review clock on the Application Status screen. */
const ProgramClockIcon = () => (
  <svg width="47" height="48" viewBox="0 0 47 48" fill="none" aria-hidden>
    <path
      d="M26.9292 3.64057C26.984 3.42943 27.0798 3.23114 27.2112 3.05702C27.3425 2.8829 27.5069 2.73636 27.6949 2.62578C27.883 2.51519 28.0909 2.44272 28.3069 2.4125C28.523 2.38228 28.7428 2.39491 28.9539 2.44966C32.4212 3.34821 35.6325 5.03947 38.3349 7.39025C41.0373 9.74103 43.157 12.6871 44.5272 15.9965C45.984 19.513 46.551 23.3344 46.1781 27.1224C45.8052 30.9104 44.5038 34.5479 42.3892 37.7128C40.2746 40.8776 37.4122 43.4721 34.0554 45.2665C30.6985 47.0608 26.951 47.9995 23.1447 47.9995C19.3384 47.9995 15.5909 47.0608 12.2341 45.2665C8.87728 43.4721 6.0148 40.8776 3.90023 37.7128C1.78567 34.5479 0.48428 30.9104 0.111358 27.1224C-0.261563 23.3344 0.305492 19.513 1.76229 15.9965L2.01974 15.3745C2.41357 14.424 2.87483 13.5028 3.4 12.6181C3.625 12.2392 3.99127 11.9653 4.41824 11.8565C4.84522 11.7477 5.29793 11.813 5.67677 12.038C6.05561 12.263 6.32956 12.6293 6.43835 13.0562C6.54714 13.4832 6.48186 13.9359 6.25686 14.3148C5.81263 15.063 5.42241 15.8421 5.0892 16.6459L4.83175 17.268C3.28655 20.9984 2.91746 25.1118 3.77387 29.0577C4.63028 33.0037 6.67135 36.594 9.62397 39.3482C12.5766 42.1025 16.2999 43.8893 20.2958 44.4697C24.2917 45.05 28.3695 44.3962 31.9837 42.5957C35.5978 40.7952 38.5759 37.9339 40.5195 34.3946C42.463 30.8553 43.2793 26.8069 42.8592 22.791C42.4391 18.7751 40.8025 14.9833 38.1685 11.923C35.5345 8.86258 32.0287 6.67961 28.1201 5.66612C27.9089 5.61138 27.7105 5.51555 27.5364 5.38412C27.3622 5.25268 27.2156 5.08821 27.105 4.9001C26.9944 4.712 26.922 4.50394 26.8918 4.28783C26.8616 4.07171 26.8744 3.85177 26.9292 3.64057Z"
      fill="url(#clock_a)"
    />
    <path
      d="M7.96716 7.47695C7.6496 7.20361 7.24045 7.06039 6.82174 7.076C6.40303 7.09161 6.00568 7.2649 5.70935 7.56112C5.41302 7.85735 5.23959 8.25463 5.22383 8.67334C5.20807 9.09205 5.35115 9.50124 5.62437 9.81891L20.4568 26.9343C20.752 27.2735 21.1139 27.5482 21.5198 27.7416C21.9257 27.9349 22.3671 28.0427 22.8165 28.0582C23.2659 28.0738 23.7136 27.9967 24.1319 27.8318C24.5503 27.667 24.9302 27.4178 25.2481 27.0999C25.5661 26.7819 25.8152 26.402 25.9801 25.9837C26.145 25.5654 26.222 25.1176 26.2065 24.6682C26.191 24.2189 26.0832 23.7775 25.8898 23.3715C25.6965 22.9656 25.4217 22.6038 25.0826 22.3085L7.96716 7.47695ZM24.2637 0.260882C23.8749 0.0933154 23.4566 0.00469882 23.0332 0.000181848C22.6098 -0.00433512 22.1897 0.0753381 21.7973 0.234571C21.405 0.393804 21.0482 0.629421 20.7477 0.927733C20.4472 1.22605 20.2089 1.5811 20.0468 1.97228C19.8847 2.36345 19.802 2.78294 19.8034 3.20637C19.8048 3.6298 19.8904 4.04872 20.0551 4.43881C20.2198 4.82889 20.4604 5.18235 20.7629 5.47865C21.0654 5.77494 21.4237 6.00817 21.8171 6.16478C22.5987 6.48173 23.4739 6.47683 24.252 6.15115C25.03 5.82548 25.6477 5.20545 25.9704 4.42622C26.2932 3.647 26.2948 2.77177 25.975 1.99135C25.6551 1.21093 25.0397 0.588611 24.2629 0.260052"
      fill="url(#clock_b)"
    />
    <defs>
      <linearGradient
        id="clock_a"
        x1="23.1447"
        y1="2.39648"
        x2="3.8809"
        y2="16.0967"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#99722E" />
        <stop offset="1" stopColor="#FFBD4C" />
      </linearGradient>
      <linearGradient
        id="clock_b"
        x1="15.7182"
        y1="0"
        x2="5.39897"
        y2="5.40862"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#99722E" />
        <stop offset="1" stopColor="#FFBD4C" />
      </linearGradient>
    </defs>
  </svg>
);

/* Matches the footprint of the supplied 24×22 icon tiles, so a lucide glyph can
   sit beside them without breaking the row's rhythm. */
const IconTile: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="text-dark-referral-accent bg-dark-referral-icon-tile flex h-[22px] w-6 items-center justify-center rounded-[5px]">
    {children}
  </span>
);

const PERKS = [
  {
    title: "Lifetime",
    body: "No expiry",
    icon: <BadgeCheck className="size-3.5" />,
  },
  { title: "Unlimited", body: "Referrals", icon: null },
];

const APPROVAL_CRITERIA = [
  "You have an active Dynascent Global account in good standing",
  "You have an audience or network you can refer (community, newsletter, social, business)",
  "You agree to our promotion guidelines and won't spam or misrepresent the product",
];

/* Shared by both gated screens — same copy, but the mocks tint the links to
   match whichever screen they sit on. */
const AgreementRow: FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  linkClass: string;
  disabled?: boolean;
}> = ({ checked, onChange, linkClass, disabled }) => (
  <div className="bg-dark-stat-surface border-dark-stat-stroke mt-3 flex items-start gap-3 rounded-xl border p-4">
    <Checkbox
      id="referral-terms"
      checked={checked}
      disabled={disabled}
      onCheckedChange={(value) => onChange(value === true)}
      className="mt-0.5 shrink-0"
    />
    <label
      htmlFor="referral-terms"
      className="text-dark-text-50 text-sm leading-relaxed"
    >
      I agree to the{" "}
      {/* TODO(referral-api): no route or sheet exists for either document yet,
          so these are inert until the destinations are defined. */}
      <span className={linkClass}>Partner Terms</span> and the{" "}
      <span className={linkClass}>Promotion Guidelines</span>. I won&apos;t
      misrepresent the product or spam users.
    </label>
  </div>
);

const JoinProgram: FC<{ feePercent: number }> = ({ feePercent }) => {
  const [agreed, setAgreed] = useState(false);
  /* The mutation invalidates the user query, so a successful apply re-renders
     this subtree as the status screen. Nothing else to do on success. */
  const apply = useApplyForReferral();

  return (
    <>
      <p className="text-dark-text-300 text-xs tracking-wide uppercase">
        Referral program
      </p>
      <h2 className="mt-1 text-2xl font-semibold">Join the program</h2>

      <div className="bg-dark-referral-hero border-dark-referral-hero-stroke relative mt-5 overflow-hidden rounded-2xl border p-5">
        <img
          src="/images/gift.webp"
          alt=""
          aria-hidden
          className="pointer-events-none absolute -top-2 -right-6 w-44 opacity-90 select-none"
        />
        <span className="text-dark-error-300 inline-flex items-center gap-1.5 rounded-full border border-current/25 bg-current/10 px-3 py-1.5 text-[11px] font-semibold tracking-wide uppercase">
          <Lock className="size-3" />
          Not yet whitelisted
        </span>

        <h3 className="mt-4 text-2xl leading-tight font-bold">
          Earn {feePercent}% on every,
          <br />
          referral <span className="text-dark-referral-accent">forever</span>.
        </h3>

        <p className="text-dark-text-300 mt-3 text-sm leading-relaxed">
          The Dynascent Global referral program is invitation-based. Apply below and
          our team will review your fit within 2&ndash;3 business days.
        </p>

        <div className="mt-5 grid grid-cols-3 gap-2.5">
          <div className="bg-dark-stat-surface border-dark-stat-stroke rounded-xl border p-3">
            <AvailableIcon />
            <p className="text-dark-text-50 mt-3 text-sm font-medium">
              {feePercent}% earnings
            </p>
            <p className="text-dark-text-300 text-xs">On deposit fees</p>
          </div>
          {PERKS.map((perk) => (
            <div
              key={perk.title}
              className="bg-dark-stat-surface border-dark-stat-stroke rounded-xl border p-3"
            >
              {perk.icon ? <IconTile>{perk.icon}</IconTile> : <ReferralsIcon />}
              <p className="text-dark-text-50 mt-3 text-sm font-medium">
                {perk.title}
              </p>
              <p className="text-dark-text-300 text-xs">{perk.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-dark-stat-surface border-dark-stat-gold-stroke mt-4 rounded-2xl border p-5">
        <h3 className="text-xl font-semibold">Who we approve</h3>
        <ul className="mt-4 space-y-4">
          {APPROVAL_CRITERIA.map((criterion) => (
            <li key={criterion} className="flex items-start gap-3">
              <CheckCircle2 className="text-dark-referral-success mt-0.5 size-5 shrink-0" />
              <span className="text-dark-text-300 text-sm leading-relaxed">
                {criterion}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <AgreementRow
        checked={agreed}
        onChange={setAgreed}
        linkClass="text-dark-referral-accent"
      />

      <div className="bg-dark-stat-surface border-dark-stat-stroke mt-3 flex items-start gap-3 rounded-xl border p-4">
        <AlertCircle className="text-dark-text-300 mt-0.5 size-4 shrink-0" />
        <p className="text-dark-text-300 text-sm leading-relaxed">
          Approval is at Dynascent Global&apos;s discretion. We typically respond
          within 2&ndash;3 business days. Approved partners get instant access
          to their referral dashboard and code.
        </p>
      </div>

      {/* Pinned to the bottom of the sheet's scroll area, as in the mock. The
          opaque backdrop is what stops content reading through it. */}
      <div className="bg-dark-background-secondary border-dark-stat-stroke sticky bottom-0 -mx-4 mt-8 border-t px-4 py-4">
        <Button
          type="button"
          disabled={!agreed || apply.isPending}
          onClick={() => apply.mutate({ agreedToTerms: true })}
          className="bg-dark-primary-gradient text-dark-text-400 flex h-12 w-full items-center justify-center gap-2 rounded-xl font-semibold tracking-wide uppercase hover:cursor-pointer hover:opacity-90 disabled:opacity-40"
        >
          {apply.isPending ? "Submitting…" : "Submit application"}
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </>
  );
};

const STATUS_BADGES: Record<
  ReferralApplicationStatus,
  { label: string; className: string }
> = {
  "under-review": {
    label: "Under review",
    className:
      "bg-dark-warning-300/10 border-dark-warning-300/25 text-dark-warning-300",
  },
  approved: {
    label: "Approved",
    className:
      "bg-dark-stat-success-surface border-dark-stat-success-stroke text-dark-referral-success",
  },
  rejected: {
    label: "Declined",
    className:
      "bg-dark-error-300/10 border-dark-error-300/25 text-dark-error-300",
  },
};

/* "Today" while it is still today, a short date after that. */
const submittedLabel = (iso?: string | null) => {
  if (!iso) return "Today";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "Today";
  return then.toDateString() === new Date().toDateString()
    ? "Today"
    : then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const ApplicationStatus: FC<{ feePercent: number }> = ({ feePercent }) => {
  const { data } = useGetReferralApplication();
  const application = data?.application ?? null;
  const status: ReferralApplicationStatus = data?.status ?? "under-review";
  const badge = STATUS_BADGES[status];

  const timeline = [
    {
      title: "Application submitted",
      body: submittedLabel(application?.createdAt),
      /* Always complete — reaching this screen means it was submitted. */
      state: "done" as const,
    },
    {
      title: "Review in progress",
      /* A decline puts the reviewer's note here; it is the only place the
         user sees why. */
      body:
        status === "rejected"
          ? (application?.reviewNote ??
            "Your application was not approved this time.")
          : status === "approved"
            ? "Review complete"
            : "Our team is evaluating your application",
      state:
        status === "under-review" ? ("active" as const) : ("done" as const),
    },
    {
      title: "Access granted",
      body: `Start earning ${feePercent}% on every referral`,
      state: status === "approved" ? ("done" as const) : ("todo" as const),
    },
  ];

  return (
    <>
      <p className="text-dark-text-300 text-xs tracking-wide uppercase">
        Referral program
      </p>
      <h2 className="mt-1 text-2xl font-semibold">Application Status</h2>

      <div className="bg-dark-stat-gold-surface border-dark-stat-gold-stroke mt-5 rounded-2xl border p-6 text-center">
        <div className="flex justify-center">
          <ProgramClockIcon />
        </div>
        <span
          className={`mt-4 inline-flex items-center rounded-full border px-3 py-1.5 text-[11px] font-semibold tracking-wide uppercase ${badge.className}`}
        >
          {badge.label}
        </span>
        <h3 className="mt-3 text-2xl font-bold">
          {status === "rejected"
            ? "Application declined"
            : status === "approved"
              ? "You're approved"
              : "Application received"}
        </h3>
        <p className="text-dark-text-300 mt-3 text-sm leading-relaxed">
          {status === "rejected" ? (
            (application?.reviewNote ??
            "Thanks for applying. Your application was not approved this time.")
          ) : status === "approved" ? (
            "Your referral dashboard is ready. Reopen this screen to start sharing your code."
          ) : (
            <>
              Thanks for applying. Our team reviews applications within{" "}
              <span className="text-dark-text-50">2&ndash;3 business days</span>
              . We&apos;ll email you the decision.
            </>
          )}
        </p>
      </div>

      <div className="bg-dark-stat-surface border-dark-stat-stroke mt-4 rounded-2xl border p-5">
        <h3 className="text-xl font-semibold">What happened next</h3>
        <ol className="mt-4">
          {timeline.map((entry, index) => (
            <li key={entry.title} className="flex gap-3">
              <div className="flex flex-col items-center">
                {entry.state === "done" ? (
                  <span className="bg-dark-referral-success text-dark-text-400 flex size-6 shrink-0 items-center justify-center rounded-full">
                    <Check className="size-3.5" strokeWidth={3} />
                  </span>
                ) : (
                  <span
                    className={`flex size-6 shrink-0 items-center justify-center rounded-full ${
                      entry.state === "active"
                        ? "bg-dark-warning-300"
                        : "bg-dark-stat-stroke"
                    }`}
                  >
                    <span
                      className={`size-2 rounded-full ${
                        entry.state === "active"
                          ? "bg-dark-warning-300"
                          : "bg-dark-text-300"
                      }`}
                    />
                  </span>
                )}
                {index < timeline.length - 1 && (
                  <span className="bg-dark-stat-stroke w-px flex-1" />
                )}
              </div>
              <div className={index < timeline.length - 1 ? "pb-6" : ""}>
                <p
                  className={
                    entry.state === "todo"
                      ? "text-dark-text-300 font-medium"
                      : "text-dark-text-50 font-medium"
                  }
                >
                  {entry.title}
                </p>
                <p className="text-dark-text-300 mt-0.5 text-sm">
                  {entry.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Locked: the agreement was accepted at submission and is shown for the
          record, not for editing. */}
      <AgreementRow
        checked
        disabled
        onChange={() => {}}
        linkClass="text-dark-primary-main"
      />

      <div className="bg-dark-stat-surface border-dark-stat-stroke mt-3 flex items-start gap-3 rounded-xl border p-4">
        <IconTile>
          <Mail className="size-3.5" />
        </IconTile>
        <div>
          <p className="text-dark-text-50 font-medium">
            Need update on your application?
          </p>
          <p className="text-dark-text-300 mt-1 text-sm leading-relaxed">
            Email us at{" "}
            <a
              href="mailto:partners@digitwallet.app"
              className="text-dark-primary-main underline-offset-2 hover:underline"
            >
              partners@digitwallet.app
            </a>{" "}
            within 24 hours and we&apos;ll work it in before review.
          </p>
          {/* TODO(referral-api): no application-detail view exists yet. */}
          <p className="text-dark-primary-main mt-2 text-sm">
            View application details
          </p>
        </div>
      </div>
    </>
  );
};

const HOW_IT_WORKS = [
  {
    title: "Share your code",
    body: "Send your referral code or link to anyone",
  },
  {
    title: "They sign up & deposit",
    body: "Friends create an account and fund their cards",
  },
];

const Referral = () => {
  const { user } = useUser();
  const feePercent = user?.referralFeePercent ?? 0;

  /* Which of the three screens renders is read off the USER object, never off
     the referral endpoints — `applyForReferral` invalidates the user query and
     `hasPendingReferralRequest` flipping true is what moves an applicant onto
     the status screen. */
  const isPartner = !!user?.canRefer;
  const hasApplied = !!user?.hasPendingReferralRequest;

  const [search, setSearch] = useState("");
  /* 1-indexed for display; the list endpoint is 0-indexed. */
  const [page, setPage] = useState(1);

  const { data: details, isLoading: detailsLoading } = useGetReferralDetails({
    enabled: isPartner,
  });
  const { data: list, isLoading: listLoading } = useGetReferralList({
    page: page - 1,
    limit: PAGE_SIZE,
    enabled: isPartner,
  });

  const withdraw = useWithdrawReferralEarnings();

  const referralCode = details?.referralCode ?? "";
  const available = details?.available ?? 0;
  const minWithdrawal = details?.minWithdrawal ?? 0;

  /* Trust the server's `canWithdraw` — it can encode rules the client can't
     see (cooldowns, KYC, a withdrawal already in flight). The local comparison
     is only a fallback for a response that omits the field. */
  const canWithdraw =
    details?.canWithdraw ?? (minWithdrawal > 0 && available >= minWithdrawal);
  const progress =
    minWithdrawal > 0 ? Math.min(100, (available / minWithdrawal) * 100) : 0;

  const referrals = useMemo(() => list?.referrals ?? [], [list]);

  /* The API returns no "top earner" flag, so it is derived: the largest
     `earnedAmount` on the page. Page-local, like the search below. */
  const topEarnerId = useMemo(() => {
    if (referrals.length === 0) return null;
    return referrals.reduce((best, current) =>
      current.earnedAmount > best.earnedAmount ? current : best,
    ).id;
  }, [referrals]);

  /* The list endpoint has no search param, so this filters the fetched page
     only — it does not reach rows on other pages. */
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return referrals;
    return referrals.filter((referral) => {
      return (
        `${referral.firstName} ${referral.lastName}`
          .toLowerCase()
          .includes(term) || referral.email.toLowerCase().includes(term)
      );
    });
  }, [referrals, search]);

  const totalPages = Math.max(1, Math.ceil((list?.total ?? 0) / PAGE_SIZE));
  /* Clamp rather than store a corrected page: a shrinking result set can
     strand `page` past the end, and resetting it in an effect would flash an
     empty list for one frame. */
  const currentPage = Math.min(page, totalPages);

  if (!isPartner) {
    return (
      <div className="text-dark-text-50 relative min-h-[calc(100dvh-80px)] pb-8">
        <div
          aria-hidden
          className="bg-dark-referral-glow pointer-events-none absolute -inset-x-4 -top-24 h-80"
        />
        <div className="relative">
          {hasApplied ? (
            <ApplicationStatus feePercent={feePercent} />
          ) : (
            <JoinProgram feePercent={feePercent} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="text-dark-text-50 relative min-h-[calc(100dvh-80px)] pb-8">
      {/* The gold bloom behind the top of the screen. Sits outside the sheet's
          horizontal padding, hence the negative insets. */}
      <div
        aria-hidden
        className="bg-dark-referral-glow pointer-events-none absolute -inset-x-4 -top-24 h-80"
      />

      <div className="relative">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Referrals</h2>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="Share your referral link"
            onClick={() => handleShare(`signup?ref=${referralCode}`)}
            className="border-dark-stroke-3 text-dark-text-100 border"
          >
            <Share2 className="size-4" />
          </Button>
        </div>

        {/* Hero */}
        <div className="bg-dark-referral-hero border-dark-referral-hero-stroke relative mt-6 overflow-hidden rounded-2xl border p-5">
          <img
            src="/images/gift.webp"
            alt=""
            aria-hidden
            className="pointer-events-none absolute -top-2 -right-6 w-44 opacity-90 select-none"
          />
          <span className="bg-dark-stat-surface border-dark-stat-stroke text-dark-referral-accent inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold tracking-wide uppercase">
            Earn {feePercent}% on deposit fee
          </span>

          <h3 className="mt-4 text-2xl leading-tight font-bold">
            Invite friends,
            <br />
            earn <span className="text-dark-referral-accent">forever.</span>
          </h3>

          <p className="text-dark-text-300 mt-3 text-sm">
            Get {feePercent}% of every deposit fee from people you refer.
          </p>

          <p className="text-dark-text-300 mt-4 text-sm">Your referral code</p>

          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            <div className="border-dark-stroke-3 flex h-10 min-w-0 flex-1 items-center justify-between gap-2 rounded-lg border border-dashed px-3">
              <span className="text-dark-text-50 overflow-hidden font-semibold tracking-wide text-ellipsis whitespace-nowrap">
                {referralCode}
              </span>
              <Copy text={referralCode} variant="white" side="left" />
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleShare(`signup?ref=${referralCode}`)}
              className="border-dark-stroke-3 text-dark-text-100 flex h-10 shrink-0 items-center gap-1.5 rounded-lg border px-3 hover:cursor-pointer"
            >
              <Link2 className="size-4" />
              <span className="text-sm">Copy link</span>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <StatCard
            icon={<ReferralsIcon />}
            label="Total referrals"
            value={detailsLoading ? "—" : `${details?.totalReferrals ?? 0}`}
            footnote={
              <span className="text-dark-text-300">
                +{details?.thisMonthReferrals ?? 0} this month
              </span>
            }
          />
          <StatCard
            icon={<EarningsIcon />}
            label="Total earnings"
            value={
              detailsLoading
                ? "—"
                : `$${formatAmount(details?.totalEarnings ?? 0)}`
            }
            footnote={
              <span className="text-dark-referral-accent">
                +${formatAmount(details?.thisMonthEarnings ?? 0)} this month
              </span>
            }
          />
          <StatCard
            icon={<AvailableIcon />}
            tone="success"
            label="Available"
            value={detailsLoading ? "—" : `$${formatAmount(available)}`}
            footnote={
              <span className="text-dark-text-300">
                {canWithdraw ? "Ready to withdraw" : "Below minimum"}
              </span>
            }
          />
          <StatCard
            icon={<WithdrawnIcon />}
            tone="gold"
            label="Total withdrawn"
            value={
              detailsLoading
                ? "—"
                : `$${formatAmount(details?.totalWithdrawn ?? 0)}`
            }
          />
        </div>

        {/* Withdraw */}
        <div className="bg-dark-stat-success-surface border-dark-stat-success-stroke mt-4 rounded-2xl border p-4">
          <div className="flex items-center justify-between gap-2">
            <WithdrawWalletIcon />
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold tracking-wide uppercase ${
                canWithdraw
                  ? "bg-dark-stat-success-surface border-dark-stat-success-stroke text-dark-referral-success border"
                  : "bg-dark-stat-surface border-dark-stat-stroke text-dark-text-300 border"
              }`}
            >
              <span
                className={`size-1.5 rounded-full ${canWithdraw ? "bg-dark-referral-success" : "bg-dark-text-300"}`}
              />
              {canWithdraw ? "Ready to withdraw" : "Keep earning"}
            </span>
          </div>

          <div
            role="progressbar"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Progress to withdrawal minimum"
            className="bg-dark-stat-surface mt-4 h-1.5 w-full overflow-hidden rounded-full"
          >
            <div
              className="bg-dark-referral-success h-full rounded-full transition-[width]"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="text-dark-text-300 mt-2 flex items-center justify-between gap-2 text-sm">
            <p>
              {canWithdraw ? (
                <>
                  You&apos;ve reached the{" "}
                  <span className="text-dark-text-50 font-semibold">
                    ${formatAmount(minWithdrawal)}
                  </span>{" "}
                  minimum
                </>
              ) : (
                <>
                  <span className="text-dark-text-50 font-semibold">
                    ${formatAmount(Math.max(0, minWithdrawal - available))}
                  </span>{" "}
                  to go
                </>
              )}
            </p>
            <span className="shrink-0">${formatAmount(minWithdrawal)} min</span>
          </div>

          <Button
            type="button"
            disabled={!canWithdraw || withdraw.isPending}
            onClick={() => withdraw.mutate()}
            className="bg-dark-referral-success text-dark-text-400 mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl font-semibold hover:cursor-pointer hover:opacity-90 disabled:opacity-50"
          >
            <WithdrawWalletIcon />
            {withdraw.isPending ? "Transferring…" : "Transfer to wallet"}
          </Button>
        </div>

        {/* Referred users */}
        <h3 className="mt-8 text-xl font-semibold">Referred users</h3>

        <div className="relative mt-3">
          <Search className="text-dark-text-300 pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search..."
            aria-label="Search referred users"
            /* `field-gradient-aux-4` carries its own fill AND its own 0.8px
               transparent border — do not add `bg-*` or `border-*`, they fight
               it. */
            className="field-gradient-aux-4 text-dark-text-50 placeholder:text-dark-text-300 h-11 w-full rounded-full pl-9"
          />
        </div>

        {listLoading ? (
          <ul className="mt-3 space-y-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <li
                key={index}
                className="bg-dark-stat-surface border-dark-stat-stroke h-[68px] animate-pulse rounded-xl border"
              />
            ))}
          </ul>
        ) : visible.length === 0 ? (
          <p className="bg-dark-stat-surface border-dark-stat-stroke text-dark-text-300 mt-3 rounded-xl border p-6 text-center text-sm">
            {referrals.length === 0
              ? "You haven't referred anyone yet."
              : "No referrals match that search."}
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {visible.map((referral) => (
              <ReferredUserRow
                key={referral.id}
                referral={referral}
                isTopEarner={referral.id === topEarnerId}
              />
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="text-dark-text-50 mt-4 flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label="Previous page"
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
              className="border-dark-stat-gold-stroke border disabled:opacity-40"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label="First page"
              disabled={currentPage === 1}
              onClick={() => setPage(1)}
              className="border-dark-stat-stroke border disabled:opacity-40"
            >
              <ChevronsLeft className="size-4" />
            </Button>

            <span className="border-dark-stat-gold-stroke rounded-md border px-4 py-2 text-sm font-medium">
              {currentPage}
            </span>
            <span className="text-dark-text-300 text-sm">Of {totalPages}</span>

            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label="Last page"
              disabled={currentPage === totalPages}
              onClick={() => setPage(totalPages)}
              className="border-dark-stat-stroke border disabled:opacity-40"
            >
              <ChevronsRight className="size-4" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label="Next page"
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage + 1)}
              className="border-dark-stat-stroke border disabled:opacity-40"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}

        {/* How it works */}
        <div className="bg-dark-stat-surface border-dark-stat-stroke mt-8 rounded-2xl border p-5">
          <h3 className="text-xl font-semibold">How it works</h3>
          <ol className="mt-4">
            {[
              ...HOW_IT_WORKS,
              {
                title: `You earn ${feePercent}%`,
                body: `Get a share of every deposit fee they pay`,
              },
            ].map((entry, index, all) => (
              <li key={entry.title} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="bg-dark-stat-gold-surface border-dark-stat-gold-stroke text-dark-primary-main flex size-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold">
                    {index + 1}
                  </span>
                  {/* The rail joining the step markers; the last step ends
                          the list, so it gets no tail. */}
                  {index < all.length - 1 && (
                    <span className="bg-dark-stat-gold-stroke w-px flex-1" />
                  )}
                </div>
                <div className={index < all.length - 1 ? "pb-5" : ""}>
                  <p className="text-dark-text-50 font-medium">{entry.title}</p>
                  <p className="text-dark-text-300 mt-0.5 text-sm">
                    {entry.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <Button
          type="button"
          onClick={() => handleShare(`signup?ref=${referralCode}`)}
          className="bg-dark-primary-gradient text-dark-text-400 mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-full font-semibold hover:cursor-pointer hover:opacity-90"
        >
          <Share2 className="size-4" />
          Share Your Link
        </Button>
      </div>
    </div>
  );
};

export default Referral;
